import * as React from 'react';
import { TrendingUp, TrendingDown, Minus, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SparklineChart } from './SparklineChart';

/* ─── Types ────────────────────────────────────────────────────────────────── */

interface KPITrend {
  direction: 'up' | 'down' | 'stable';
  value: string;
}

interface KPICardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  value: string | number;
  trend?: KPITrend;
  icon?: LucideIcon;
  sparkline?: number[];
  children?: React.ReactNode;
}

/* ─── Trend config ─────────────────────────────────────────────────────────── */

const trendConfig: Record<
  KPITrend['direction'],
  { icon: React.ElementType; className: string }
> = {
  up: { icon: TrendingUp, className: 'text-raios-success-text' },
  down: { icon: TrendingDown, className: 'text-raios-danger-text' },
  stable: { icon: Minus, className: 'text-raios-muted' },
};

/* ─── KPICard ──────────────────────────────────────────────────────────────── */

const KPICard = React.forwardRef<HTMLDivElement, KPICardProps>(
  ({ label, value, trend, icon: Icon, sparkline, children, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'relative bg-raios-card rounded-lg p-6 shadow-card',
          'hover:shadow-card-hover hover:-translate-y-px transition-[transform,box-shadow] duration-200',
          className
        )}
        {...props}
      >
        {/* Icon — top right */}
        {Icon && (
          <Icon className="absolute top-6 right-6 h-5 w-5 text-raios-muted shrink-0" aria-hidden="true" />
        )}

        {/* Label — top left */}
        <span className="text-[10px] uppercase font-semibold tracking-[0.15em] text-raios-muted text-balance">
          {label}
        </span>

        {/* Value */}
        <div className="mt-2">
          <span className="font-display text-3xl font-bold text-raios-text tracking-tight tabular-nums">
            {value}
          </span>
        </div>

        {/* Trend */}
        {trend && (
          <div className="mt-1">
            <TrendIndicator trend={trend} />
          </div>
        )}

        {/* Children slot */}
        {children}

        {/* Sparkline */}
        {sparkline && sparkline.length > 0 && (
          <div className="mt-4">
            <SparklineChart data={sparkline} height={32} />
          </div>
        )}
      </div>
    );
  }
);

KPICard.displayName = 'KPICard';

/* ─── TrendIndicator ───────────────────────────────────────────────────────── */

function TrendIndicator({ trend }: { trend: KPITrend }) {
  const config = trendConfig[trend.direction];
  const TrendIcon = config.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium',
        config.className
      )}
    >
      <TrendIcon className="h-3.5 w-3.5" aria-hidden="true" />
      {trend.value}
    </span>
  );
}

export { KPICard };
export type { KPICardProps, KPITrend };
